import {
  ChargingStation as ApiChargingStation,
  CreateChargingStation,
  ImportChargingStationsResponse as ImportChargingStationsResponseApi
} from '@infuse/api/e-connector/charging-station';
import { BasicAuthStatus, ChargingStation, ImportChargeStationsResponse } from './model';
import { enumFromStringValue } from '@infuse/utils';
import { ConnectionType, OcppVersion } from '@infuse/e-connector/central-system/domain';
import { mapLinkedCentralSystemToApi, mapLinkedCentralSystemToDomain } from '@infuse/e-connector/shared/utils';

export const mapToDomain = (apiChargingStation: ApiChargingStation): ChargingStation => {
  return {
    id: apiChargingStation.id,
    name: apiChargingStation.name,
    alias: apiChargingStation.linkedCentralSystems
      .filter((lcs) => lcs.customChargePointName != null)
      .map((lcs) => lcs.customChargePointName),
    type: enumFromStringValue(ConnectionType, apiChargingStation.type),
    ocppVersion: enumFromStringValue(OcppVersion, apiChargingStation.ocppVersion),
    uri: apiChargingStation.uri,
    useManualURI: apiChargingStation.useManualURI,
    useRequestHeader: apiChargingStation.useRequestHeader,
    omitSoapHeader: apiChargingStation.omitSoapHeader,
    linkedCentralSystems: apiChargingStation.linkedCentralSystems.map(mapLinkedCentralSystemToDomain),
    basicAuthStatus: enumFromStringValue(BasicAuthStatus, apiChargingStation.basicAuthStatus),
    basicAuthPassword: apiChargingStation.basicAuthPassword,
    basicAuthDeviationId: apiChargingStation.basicAuthDeviationId,
    networkProfileId: apiChargingStation.networkProfileId,
    networkProfileName: apiChargingStation.networkProfileName,
    createdAt: new Date(apiChargingStation.createdAt),
    updatedAt: new Date(apiChargingStation.updatedAt),
    basicAuthLastPasswordChange: new Date(apiChargingStation.basicAuthLastPasswordChange)
  };
};

export const mapToApi = (chargingStation: ChargingStation): ApiChargingStation => {
  chargingStation = normalizeChargingStationForApi(chargingStation);
  return {
    id: chargingStation.id,
    name: chargingStation.name,
    type: chargingStation.type,
    ocppVersion: chargingStation.ocppVersion,
    uri: chargingStation.uri,
    useManualURI: chargingStation.useManualURI,
    useRequestHeader: chargingStation.useRequestHeader,
    omitSoapHeader: chargingStation.omitSoapHeader,
    linkedCentralSystems: chargingStation.linkedCentralSystems.map(mapLinkedCentralSystemToApi),
    basicAuthStatus: chargingStation.basicAuthStatus,
    basicAuthPassword: chargingStation.basicAuthPassword,
    basicAuthDeviationId: chargingStation.basicAuthDeviationId,
    networkProfileId: chargingStation.networkProfileId,
    networkProfileName: chargingStation.networkProfileName,
    createdAt: chargingStation.createdAt,
    updatedAt: chargingStation.updatedAt,
    basicAuthLastPasswordChange: chargingStation.basicAuthLastPasswordChange
  };
};

export const mapImportedChargingStationsToDomain = (
  importChargingStations: ImportChargingStationsResponseApi
): ImportChargeStationsResponse => {
  return {
    imported: importChargingStations.imported.map((i) => ({
      name: i.name,
      centralSystemName: i.centralSystemName,
      customName: i.customName,
      commandsAllowed: i.commandsAllowed,
      sessionAuthorizationAllowed: i.sessionAuthorizationAllowed
    })),
    failed: importChargingStations.failed.map((f) => ({
      chargingStation: {
        name: f.chargePoint.name,
        centralSystemName: f.chargePoint.centralSystemName,
        customName: f.chargePoint.customName,
        commandsAllowed: f.chargePoint.commandsAllowed,
        sessionAuthorizationAllowed: f.chargePoint.sessionAuthorizationAllowed
      },
      error: f.error
    }))
  };
};

/**
 * Normalizes the charging station for the API. This is necessary because the API has some *very* specific requirements for the charging station object.
 * @param chargingStation
 */
export const normalizeChargingStationForApi = (
  chargingStation: ChargingStation | CreateChargingStation
): ChargingStation => {
  if (chargingStation.type !== ConnectionType.SOAP) {
    // SOAP is the only type that can have an uri
    chargingStation.uri = undefined;
  }

  if (!chargingStation.uri) {
    // if no uri is set, we need to set the useManualURI to false
    chargingStation.uri = undefined;
    chargingStation.useManualURI = false;
  } else {
    // if an uri is set, we need to set the useManualURI to true
    chargingStation.useManualURI = true;
    chargingStation.useRequestHeader = false;
  }

  if (chargingStation.basicAuthPassword === null || chargingStation.basicAuthPassword === '') {
    chargingStation.basicAuthPassword = undefined;
  }

  if (
    chargingStation.basicAuthDeviationId === null ||
    chargingStation.basicAuthDeviationId === '' ||
    chargingStation.basicAuthDeviationId === '-1'
  ) {
    // if the basicAuthDeviationId is -1 (default), we need to set it to undefined
    chargingStation.basicAuthDeviationId = undefined;
  }

  return chargingStation as ChargingStation;
};
